import { sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { CartSectionRestoredEvent } from '@theme/events';
import { CartItemsComponent } from '@theme/component-cart-items';

if (document.querySelector('#cart-drawer')) {
  const panel = document.querySelector('#cart-drawer dialog');
  if (panel && typeof ResizeObserver !== 'undefined') {
    const layoutObserver = new ResizeObserver(() => {
      panel.querySelector('cart-items-component')?.dispatchEvent(new CartSectionRestoredEvent());
    });
    layoutObserver.observe(panel);
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'remove' || !event.target?.closest?.('#cart-drawer')) return;
    const focused = document.activeElement;
    void Promise.allSettled(CartItemsComponent.pendingQuantityUpdates).then(() => {
      if (!panel?.open || focused?.isConnected || document.activeElement !== document.body) return;
      panel.querySelector('.theme-drawer__close-button')?.focus({ preventScroll: true });
    });
  });

  let pendingSync = null;
  let syncGeneration = 0;
  const SYNC_TIMEOUT_MS = 8000;
  let openVersion = 0;
  let closingForNative = false;
  let stagedVersion = 0;
  let stageTimer;
  let expiredVersion = 0;
  const clearStage = (version) => {
    if (version && version !== stagedVersion) return;
    document.querySelector('#slidecarthq')?.removeAttribute('data-ggx-cart-stage');
    const status = document.querySelector('.ggx-cart-handoff-status');
    if (status) status.hidden = true;
    clearTimeout(stageTimer);
    stageTimer = undefined;
    stagedVersion = 0;
  };
  const stageHandoff = (version) => {
    stagedVersion = version;
    document.querySelector('#slidecarthq')?.setAttribute('data-ggx-cart-stage', String(version));
    const status = document.querySelector('.ggx-cart-handoff-status');
    if (status) status.hidden = false;
    // A late native refresh must not replace an app drawer already revealed as fallback.
    if (!stageTimer) stageTimer = setTimeout(() => {
      expiredVersion = Math.max(expiredVersion, stagedVersion);
      clearStage();
    }, 1000);
  };
  for (const event of ['pagehide', 'pageshow', 'error', 'unhandledrejection']) {
    window.addEventListener(event, () => {
      expiredVersion = Math.max(expiredVersion, stagedVersion);
      clearStage();
    });
  }

  const syncNativeCart = (version) => {
    if (version !== openVersion) return Promise.resolve(null);
    if (pendingSync) return pendingSync.catch(() => {}).then(() => version === openVersion ? syncNativeCart(version) : null);
    const generation = ++syncGeneration;
    const abortController = new AbortController();
    let active = true;
    let timeout;
    const isCurrent = () => active && generation === syncGeneration && version === openVersion;
    const deadline = new Promise((_, reject) => {
      timeout = setTimeout(() => {
        active = false;
        reject(new Error('Cart refresh timed out'));
        abortController.abort();
      }, SYNC_TIMEOUT_MS);
    });
    const refresh = (async () => {
      await customElements.whenDefined('theme-drawer');
      if (!isCurrent()) return null;
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      if (!isCurrent()) return null;
      const [response, html] = await Promise.all([fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: abortController.signal,
      }), sectionRenderer.renderSection('cart-drawer-section', {
        cache: false,
        mode: 'hydration',
        injectStylesheet: true,
        shouldRender: isCurrent,
      })]);
      if (!isCurrent()) return null;
      if (!response.ok) throw new Error('Cart could not be refreshed');
      const cart = await response.json();
      if (!isCurrent()) return null;
      if (!html.includes('cart-drawer-inner')) throw new Error('Cart drawer could not be refreshed');
      const drawer = document.querySelector('#cart-drawer');
      const items = drawer?.querySelector('cart-items-component');
      if (!drawer || !items || typeof drawer.open !== 'function') throw new Error('Cart drawer is unavailable');
      items.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'ggx-cart-drawer-bridge', itemCount: cart.item_count },
        }),
      }));
      if (!isCurrent()) return null;
      items.dispatchEvent(new CartSectionRestoredEvent());
      return drawer;
    })();
    const sync = Promise.race([refresh, deadline]).finally(() => {
      active = false;
      clearTimeout(timeout);
      abortController.abort();
      // A late refresh must never release a newer attempt's lock.
      if (pendingSync === sync) pendingSync = null;
    });
    pendingSync = sync;
    return sync;
  };

  const previousOpened = window.SLIDECART_OPENED;
  const previousClosed = window.SLIDECART_CLOSED;

  window.SLIDECART_OPENED = function (...args) {
    const version = ++openVersion;
    stageHandoff(version);
    let result;
    try {
      result = previousOpened?.apply(this, args);
    } finally {
      void syncNativeCart(version).then((drawer) => {
        if (!drawer || version !== openVersion || (version <= expiredVersion && !drawer.isOpen) || typeof window.SLIDECART_CLOSE !== 'function') {
          clearStage(version);
          return;
        }
        closingForNative = true;
        try {
          window.SLIDECART_CLOSE();
          drawer.open();
        } finally {
          closingForNative = false;
          clearStage(version);
        }
      }).catch((error) => {
        if (version !== openVersion) return;
        clearStage(version);
        document.dispatchEvent(new CartErrorEvent({
          error: error?.message || 'Cart could not be refreshed',
          code: 'SERVICE_UNAVAILABLE',
        }));
      });
    }
    return result;
  };

  window.SLIDECART_CLOSED = function (...args) {
    if (!closingForNative) {
      openVersion += 1;
      clearStage();
    }
    return previousClosed?.apply(this, args);
  };
}
